import { useEffect, useState, useCallback } from 'react';
import { logger } from '../utils/logger';
import { UnifiedNotificationService } from '../services/UnifiedNotificationService';
import EnhancedSocketService from '../services/unifiedSocketService';
import { NOTIFICATION_EVENTS } from '../constants/notificationEvents';
import { useAuth } from './useAuth';

export interface UnifiedNotification {
  id: string;
  userId: string | number;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'emergency' | 'urgent' | 'normal' | 'danger' | 'message' | 'call';
  read: boolean;
  createdAt?: string;
  date?: string;
  messageType?: string;
  timestamp?: string;
  callId?: string | number;
}

interface UnifiedMessage {
  id: string | number;
  senderId: string | number;
  receiverId: string | number;
  content: string;
  timestamp: string;
  read?: boolean;
}

interface PresencePayload {
  userId: string | number;
  status: 'ONLINE' | 'OFFLINE' | 'AWAY';
}

interface IncomingCallPayload {
  callId: string | number;
  from: string | number;
}

interface ConnectionStatusPayload {
  isConnected: boolean;
}

/**
 * Hook unifié pour la gestion des notifications et messages
 * Remplace tous les hooks de notification fragmentés
 */
export const useUnifiedNotifications = () => {
    const [notifications, setNotifications] = useState<UnifiedNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
    
    const socket = EnhancedSocketService.getSocket();
    const { user } = useAuth();

    const syncLocalState = useCallback(() => {
        const allNotifs = UnifiedNotificationService.getNotifications();
        // Convert generic NotificationPayload to UnifiedNotification (mapping type if necessary)
        const mappedNotifs: UnifiedNotification[] = allNotifs.map(n => ({
            id: n.id,
            userId: n.userId,
            title: n.title,
            message: n.message,
            type: n.type as UnifiedNotification['type'],
            read: n.read,
            createdAt: n.createdAt,
        }));
        setNotifications(mappedNotifs);
    }, []);

    // Initialiser le service unifié
    useEffect(() => {
        if (socket && user?.id) {
            UnifiedNotificationService.initialize(socket, user.id);
            
            // Charger les notifications persistantes locales puis synchroniser avec le backend
            UnifiedNotificationService.loadFromIndexedDB().then(() => {
                syncLocalState();
                
                // Charger depuis l'API backend pour récupérer les notifications manquées
                UnifiedNotificationService.fetchNotifications().then(() => {
                    syncLocalState();
                });
            });
            
            logger.info('🚀 Hook useUnifiedNotifications initialisé avec sync API', { 
                userId: user.id,
                socketId: socket.id 
            });
        }
    }, [socket, user?.id, syncLocalState]);

    // Configurer les écouteurs
    useEffect(() => {
        if (!socket) return;

        // Messages reçus
        const handleNewMessage = (message: UnifiedMessage) => {
            logger.info('📨 Nouveau message reçu', { messageId: message.id });
            
            setNotifications(prev => {
                const exists = prev.find(n => n.id === String(message.id));
                if (!exists) {
                    const newNotif: UnifiedNotification = {
                        id: String(message.id),
                        userId: user?.id || 0,
                        title: 'Nouveau message',
                        message: message.content,
                        type: 'info',
                        read: false,
                        createdAt: message.timestamp,
                    };
                    return [newNotif, ...prev.slice(0, 49)]; // Limiter à 50
                }
                return prev;
            });
        };

        // Notifications système
        const handleNewNotification = (notification: UnifiedNotification | null) => {
            logger.info('🔔 Nouvelle notification reçue', { 
                notificationId: notification?.id,
                type: notification?.type 
            });
            
            if (!notification) {
                syncLocalState();
                return;
            }
            
            setNotifications(prev => {
                const exists = prev.find(n => n.id === notification.id);
                if (!exists) {
                    return [notification, ...prev.slice(0, 49)];
                }
                return prev;
            });
        };

        // Mise à jour présence
        const handlePresenceUpdate = (payload: PresencePayload) => {
            logger.debug('👤 Mise à jour présence', { 
                userId: payload.userId, 
                status: payload.status 
            });
        };

        // Appels entrants
        const handleIncomingCall = (call: IncomingCallPayload) => {
            logger.info('📞 Appel entrant', { 
                callId: call.callId, 
                from: call.from 
            });
            
            // Créer notification d'appel
            UnifiedNotificationService.createNotification({
                userId: user?.id || 0,
                title: 'Appel entrant',
                message: `Appel de l'utilisateur ${call.from}`,
                type: 'info',
                read: false,
                createdAt: new Date().toISOString(),
            });
        };

        // État de connexion
        const handleConnectionChange = (status: ConnectionStatusPayload) => {
            logger.info('🔌 Changement statut connexion', { status });
            setConnectionStatus(status.isConnected ? 'connected' : 'disconnected');
        };

        // Inscrire les écouteurs
        UnifiedNotificationService.on(NOTIFICATION_EVENTS.MESSAGE_RECEIVED, handleNewMessage as (...args: unknown[]) => void);
        UnifiedNotificationService.on(NOTIFICATION_EVENTS.NOTIFICATION_NEW, handleNewNotification as (...args: unknown[]) => void);
        UnifiedNotificationService.on(NOTIFICATION_EVENTS.USER_ONLINE, handlePresenceUpdate as (...args: unknown[]) => void);
        UnifiedNotificationService.on(NOTIFICATION_EVENTS.CALL_INCOMING, handleIncomingCall as (...args: unknown[]) => void);
        
        // Écouter les changements de connexion du socket
        socket.on('connect', () => handleConnectionChange({ isConnected: true }));
        socket.on('disconnect', () => handleConnectionChange({ isConnected: false }));

        // Nettoyage
        return () => {
            UnifiedNotificationService.off(NOTIFICATION_EVENTS.MESSAGE_RECEIVED, handleNewMessage as (...args: unknown[]) => void);
            UnifiedNotificationService.off(NOTIFICATION_EVENTS.NOTIFICATION_NEW, handleNewNotification as (...args: unknown[]) => void);
            UnifiedNotificationService.off(NOTIFICATION_EVENTS.USER_ONLINE, handlePresenceUpdate as (...args: unknown[]) => void);
            UnifiedNotificationService.off(NOTIFICATION_EVENTS.CALL_INCOMING, handleIncomingCall as (...args: unknown[]) => void);
        };
    }, [socket, user?.id, syncLocalState]);

    // Calculer le nombre de non lus
    useEffect(() => {
        const unread = notifications.filter(n => !n.read).length;
        setUnreadCount(unread);
    }, [notifications]);

    // Actions utilisateur
    const sendMessage = useCallback(async (receiverId: string | number, content: string, messageType = 'TEXT') => {
        try {
            const success = await UnifiedNotificationService.sendMessage({
                senderId: user?.id || 0,
                receiverId,
                content,
                messageType: messageType as "TEXT" | "IMAGE" | "FILE" | "AUDIO" | "VIDEO" | undefined,
            });

            if (!success) {
                throw new Error('Échec envoi message');
            }

            logger.info('✅ Message envoyé avec succès', { receiverId, messageType });
        } catch (error) {
            logger.error('❌ Erreur envoi message', { error, receiverId });
            throw error;
        }
    }, [user?.id]);

    const markAsRead = useCallback(async (notificationId: string) => {
        try {
            const success = await UnifiedNotificationService.markNotificationAsRead(notificationId);
            
            if (success) {
                setNotifications(prev => 
                    prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
                );
            }
        } catch (error) {
            logger.error('❌ Erreur marquage lecture', { error, notificationId });
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        const unreadNotifications = notifications.filter(n => !n.read);
        
        for (const notification of unreadNotifications) {
            await markAsRead(notification.id);
        }
    }, [notifications, markAsRead]);

    const clearNotifications = useCallback(() => {
        setNotifications([]);
        setUnreadCount(0);
        logger.info('🗑️ Notifications effacées');
    }, []);

    const updatePresence = useCallback((status: 'ONLINE' | 'AWAY' | 'OFFLINE') => {
        UnifiedNotificationService.updatePresence(status);
    }, []);

    const initiateCall = useCallback(async (to: number, provider = 'default') => {
        try {
            const success = await UnifiedNotificationService.initiateCall({
                callerId: user?.id || 0,
                receiverId: to,
                type: 'video',
            });

            if (!success) {
                throw new Error('Échec initiation appel');
            }

            logger.info('📞 Appel initié', { to, provider });
        } catch (error) {
            logger.error('❌ Erreur initiation appel', { error, to });
            throw error;
        }
    }, [user?.id]);

    return {
        // État
        notifications,
        unreadCount,
        connectionStatus,
        isConnected: connectionStatus === 'connected',
        
        // Actions
        sendMessage,
        markAsRead,
        markAllAsRead,
        clearNotifications,
        updatePresence,
        initiateCall,
        
        // Utilitaires
        getUnreadNotifications: () => notifications.filter(n => !n.read),
        getNotificationById: (id: string) => notifications.find(n => n.id === id),
        clearNotification: (id: string) => setNotifications(prev => prev.filter(n => n.id !== id)),
    };
};
